import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getPaymentMethodsReport } from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { formatDualCurrency, formatDate } from '../utils/formatters';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const PaymentMethodsReport = () => {
  const { stores } = useStores();
  const { exchangeRate } = useExchangeRate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    storeId: '',
    startDate: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    endDate: new Date(),
    methods: []
  });

  const paymentMethods = [
    { key: 'pos_banesco', label: 'POS Banesco', color: '#3B82F6' },
    { key: 'pos_bdv', label: 'POS BDV', color: '#10B981' },
    { key: 'pos_bnc', label: 'POS BNC', color: '#F43F5E' },
    { key: 'pos_banplus', label: 'POS Banplus', color: '#F59E0B' },
    { key: 'pos_provincial', label: 'POS Provincial', color: '#8B5CF6' },
    { key: 'cash_usd', label: 'Efectivo USD', color: '#06B6D4' },
    { key: 'cash_ves', label: 'Efectivo VES', color: '#84CC16' },
    { key: 'zelle_transfer', label: 'Zelle/Transferencia', color: '#EC4899' },
    { key: 'biopago', label: 'Biopago', color: '#F97316' },
    { key: 'cashea', label: 'Cashea', color: '#6B7280' },
    { key: 'pago_movil_banesco', label: 'Pago Móvil Banesco', color: '#EF4444' },
    { key: 'pago_movil_bdv', label: 'Pago Móvil BDV', color: '#A855F7' },
    { key: 'pago_movil_bnc', label: 'Pago Móvil BNC', color: '#0EA5E9' },
    { key: 'pago_movil_banplus', label: 'Pago Móvil Banplus', color: '#14B8A6' },
    { key: 'credito', label: 'Crédito', color: '#FB923C' }
  ];

  const fetchReport = async () => {
    try {
      setLoading(true);
      const filterParams = {
        storeId: filters.storeId || null,
        startDate: filters.startDate ? filters.startDate.toISOString().split('T')[0] : null,
        endDate: filters.endDate ? filters.endDate.toISOString().split('T')[0] : null,
        methods: filters.methods
      };
      
      const data = await getPaymentMethodsReport(filterParams);
      setReportData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching payment methods report:', err);
      setError('Error al cargar el reporte de métodos de pago');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleMethodToggle = (methodKey) => {
    setFilters(prev => ({
      ...prev,
      methods: prev.methods.includes(methodKey)
        ? prev.methods.filter(m => m !== methodKey)
        : [...prev.methods, methodKey]
    }));
  };

  const getMethodColor = (methodKey) => {
    const method = paymentMethods.find(m => m.key === methodKey);
    return method ? method.color : '#6B7280';
  };

  const preparePieChartData = (storeData) => {
    const data = [];
    Object.entries(storeData.months).forEach(([monthKey, monthData]) => {
      Object.entries(monthData.payment_methods).forEach(([methodKey, methodData]) => {
        const existingEntry = data.find(item => item.name === methodData.method_label);
        if (existingEntry) {
          existingEntry.value += methodData.amount_ves;
        } else {
          data.push({
            name: methodData.method_label,
            value: methodData.amount_ves,
            color: getMethodColor(methodKey)
          });
        }
      });
    });
    return data.sort((a, b) => b.value - a.value);
  };

  const prepareBarChartData = (storeData) => {
    const data = [];
    Object.entries(storeData.months).forEach(([monthKey, monthData]) => {
      const monthEntry = {
        month: monthData.month_label,
        ...Object.fromEntries(
          Object.entries(monthData.payment_methods).map(([methodKey, methodData]) => [
            methodKey,
            methodData.amount_ves
          ])
        )
      };
      data.push(monthEntry);
    });
    return data.sort((a, b) => new Date(a.month) - new Date(b.month));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Generando reporte de métodos de pago...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">💳 Reporte de Métodos de Pago</h1>
          <p className="text-gray-600 mt-1">
            Análisis detallado de ingresos por método de pago y tienda
          </p>
        </div>
        <button onClick={fetchReport} className="btn-primary">
          🔄 Actualizar Reporte
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">🔍 Filtros</h3>
        </div>
        <div className="space-y-4">
          {/* Date and Store Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Tienda</label>
              <select
                value={filters.storeId}
                onChange={(e) => setFilters(prev => ({ ...prev, storeId: e.target.value }))}
                className="form-select"
              >
                <option value="">Todas las tiendas</option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Fecha Desde</label>
              <DatePicker
                selected={filters.startDate}
                onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
                className="form-input"
                dateFormat="dd/MM/yyyy"
              />
            </div>
            <div>
              <label className="form-label">Fecha Hasta</label>
              <DatePicker
                selected={filters.endDate}
                onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
                className="form-input"
                dateFormat="dd/MM/yyyy"
                minDate={filters.startDate}
              />
            </div>
          </div>

          {/* Payment Methods Filter */}
          <div>
            <label className="form-label">Métodos de Pago</label>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 mt-2">
              {paymentMethods.map(method => (
                <label key={method.key} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.methods.includes(method.key)}
                    onChange={() => handleMethodToggle(method.key)}
                    className="rounded border-gray-300"
                  />
                  <div 
                    className="w-3 h-3 rounded"
                    style={{ backgroundColor: method.color }}
                  ></div>
                  <span className="text-sm text-gray-700">{method.label}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {filters.methods.length === 0 ? 'Todos los métodos seleccionados' : `${filters.methods.length} método(s) seleccionado(s)`}
            </p>
          </div>

          <div className="flex justify-end">
            <button onClick={fetchReport} className="btn-primary">
              📊 Generar Reporte
            </button>
          </div>
        </div>
      </div>

      {/* Report Results */}
      {reportData && Object.keys(reportData.stores).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(reportData.stores).map(([storeId, storeData]) => (
            <div key={storeId} className="card">
              <div className="card-header">
                <h3 className="card-title">🏪 {storeData.store_name}</h3>
                <div className="text-right">
                  <div className="text-lg font-bold text-green-600">
                    ${storeData.store_total_usd?.toFixed(2)}
                  </div>
                  <div className="text-sm text-blue-600">
                    Bs. {storeData.store_total_ves?.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Pie Chart */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">📊 Distribución por Método</h4>
                  <ResponsiveContainer width="100%" height={340}>
                    <PieChart>
                      <Pie
                        data={preparePieChartData(storeData)}
                        cx="50%"
                        cy="45%"
                        outerRadius={90}
                        fill="#8884d8"
                        dataKey="value"
                        labelLine={false}
                        label={({ percent }) =>
                          percent >= 0.04 ? `${(percent * 100).toFixed(1)}%` : ''
                        }
                      >
                        {preparePieChartData(storeData).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [`Bs. ${value.toFixed(2)}`, name]} />
                      <Legend
                        layout="horizontal"
                        verticalAlign="bottom"
                        align="center"
                        iconType="circle"
                        iconSize={8}
                        formatter={(value) => <span style={{ fontSize: 11 }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Bar Chart */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">📈 Evolución Mensual</h4>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={prepareBarChartData(storeData)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `Bs. ${(value / 1000).toFixed(0)}K`} />
                      <Tooltip 
                        formatter={(value, name) => [
                          `Bs. ${value.toFixed(2)}`, 
                          reportData.payment_method_labels[name] || name
                        ]}
                      />
                      {paymentMethods.map(method => (
                        <Bar 
                          key={method.key}
                          dataKey={method.key} 
                          stackId="methods"
                          fill={method.color}
                          name={method.label}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Detailed Table */}
              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Mes</th>
                      <th>Método de Pago</th>
                      <th>Monto Original</th>
                      <th>USD</th>
                      <th>VES</th>
                      <th>% del Mes</th>
                      <th>Transacciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(storeData.months).map(([monthKey, monthData]) => (
                      Object.entries(monthData.payment_methods).map(([methodKey, methodData], index) => (
                        <tr key={`${monthKey}-${methodKey}`}>
                          {index === 0 && (
                            <td rowSpan={Object.keys(monthData.payment_methods).length} className="bg-gray-50 font-medium">
                              {monthData.month_label}
                            </td>
                          )}
                          <td>
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-3 h-3 rounded"
                                style={{ backgroundColor: getMethodColor(methodKey) }}
                              ></div>
                              <span>{methodData.method_label}</span>
                            </div>
                          </td>
                          <td className="font-mono">
                            {methodData.currency === 'USD' ? '$' : 'Bs. '}
                            {methodData.amount_original.toFixed(2)}
                          </td>
                          <td className="font-mono text-green-600">
                            ${methodData.amount_usd.toFixed(2)}
                          </td>
                          <td className="font-mono text-blue-600">
                            Bs. {methodData.amount_ves.toFixed(2)}
                          </td>
                          <td className="text-right">
                            <span className="text-sm font-medium">
                              {methodData.percentage.toFixed(1)}%
                            </span>
                          </td>
                          <td className="text-center">
                            <span className="bg-gray-100 px-2 py-1 rounded text-sm">
                              {methodData.transaction_count}
                            </span>
                          </td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      ) : reportData ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📊</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No hay datos para mostrar
          </h3>
          <p className="text-gray-600 mb-4">
            No se encontraron ventas con desglose de métodos de pago en el período seleccionado.
          </p>
          <p className="text-sm text-gray-500">
            Asegúrate de haber registrado ventas usando el nuevo formato con desglose de pagos.
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default PaymentMethodsReport;